import { decoded, openOutput, play } from "./fuxi-audio.js";
import { tilePlaced, tileSaidAttribute } from "./fuxi-tile-moves.js";
import { yufaMarked } from "./lianxi-yufa-item-view.js";

/**
 * What a signed-in reader's grammar exercise plays, as 复习 plays it: each
 * tile's word as it lands on the line, and the whole sentence once an item is
 * answered right.
 *
 * **The sentence follows the right note** (Hugh, CB-23). It plays once the
 * note `./lianxi-verdict-sounds.ts` plays has finished, in one of its two
 * voices at random, so the reader hears the sentence they have just made as a
 * reward for making it. A sentence recorded in one voice only plays that
 * one. A miss plays the wrong note alone, since the sentence
 * shown then is the right one and not the reader's.
 *
 * The addresses are written onto the block by `lianxi-place.ts`, from the
 * handler's answer, so a signed-out reader's exercise has none and stays
 * silent. A tile names its word under the attribute a word-order card's tile
 * does (`fuxi-tile-moves.ts` fires the event either way), and an item names
 * its sentence under `data-cb-yufa-say`.
 *
 * **One sound at a time.** A new tile or a marked item stops what was
 * playing, as on a card, so a reader placing tiles quickly hears each word
 * and not a pile of them, and a recording that arrives after a newer one was
 * asked for is not played at all. A recording that cannot be fetched or
 * decoded plays nothing.
 */

/** Where an item names its sentence's recording. */
export const sentenceSaidAttribute = "data-cb-yufa-say";

/** How loudly a recording plays, as 复习 plays a word. */
const volume = 1;

/** How long the right note lasts, in seconds, which the sentence waits for. */
const afterNote = 0.65;

/**
 * The same sentence's recording in a voice chosen at random. The handler names
 * one voice for the whole exercise, and its address names the voice.
 */
export function eitherVoice(
  address: string,
  random: () => number = Math.random,
): string {
  return address.replace(
    /_(?:male|female)\.mp3$/u,
    random() < 0.5 ? "_female.mp3" : "_male.mp3",
  );
}

/**
 * Play the block's recordings as the reader works through it.
 * @param block - The exercise's block.
 */
export function soundYufa(block: HTMLElement): void {
  let output: AudioContext | undefined;
  let playing: AudioBufferSourceNode | undefined;
  let latest = 0;
  const fetched = new Map<string, Promise<AudioBuffer | undefined>>();

  // The first of `addresses` that fetches and decodes is played, so a voice a
  // sentence was never recorded in falls back to the one the handler named.
  const say = (
    address: string | null,
    delay = 0,
    ...fallbacks: readonly string[]
  ): void => {
    if (address === null || address === "") {
      return;
    }

    output ??= openOutput();

    const built = output;

    if (built === undefined) {
      return;
    }

    if (built.state !== "running") {
      void built.resume().catch(() => undefined);
    }

    const load = (each: string): Promise<AudioBuffer | undefined> => {
      const loading = fetched.get(each) ?? decoded(built, each);

      fetched.set(each, loading);

      return loading;
    };
    const buffer = [address, ...fallbacks].reduce<
      Promise<AudioBuffer | undefined>
    >(
      (earlier, each) => earlier.then((samples) => samples ?? load(each)),
      Promise.resolve(undefined),
    );

    latest += 1;

    const asked = latest;

    playing?.stop();
    playing = undefined;
    void buffer.then((samples) => {
      if (samples !== undefined && asked === latest) {
        playing?.stop();
        playing = play({
          output: built,
          buffer: samples,
          at: built.currentTime + delay,
          gain: volume,
        });
      }
    });
  };

  block.addEventListener(tilePlaced, (event: Event) => {
    say((event.target as Element).getAttribute(tileSaidAttribute));
  });
  block.addEventListener(yufaMarked, (event: Event) => {
    const { passed, unverified } =
      (event as CustomEvent<{ passed?: boolean; unverified?: boolean }>)
        .detail ?? {};
    const address = (event.target as Element).getAttribute(
      sentenceSaidAttribute,
    );

    if (passed === true && unverified !== true && address !== null) {
      say(eitherVoice(address), afterNote, address);
    }
  });
}
